'use client';

import { useRouter } from 'next/navigation';
import { memo, useCallback, useEffect, useMemo } from 'react';

import { Align, IconName, Tab, TabList, Tabs } from '@bytel/trilogy-react';

import { JOURNEYS } from '@app-types/product';

import { VariationEnum } from '@helpers/abtest-utils';
import { replaceWindowUrl } from '@helpers/document';
import { paths } from '@helpers/path';

import { usePageTracking } from '@landing/hooks';
import { cartStore } from '@stores/cart';

type TabsConfig = { label: string; icon: IconName; key: JOURNEYS; path: string };

const tabsContent: TabsConfig[] = [
    {
        label: 'Forfaits mobile',
        icon: IconName.SIM_CARD,
        key: JOURNEYS.BYOU,
        path: paths.plans,
    },
    {
        label: 'Offres fibre',
        icon: IconName.BOX_ULTYM,
        key: JOURNEYS.FAI,
        path: paths.fai,
    },
    {
        label: 'Packs divertissement',
        icon: IconName.ENTERTAINMENT,
        key: JOURNEYS.PACKS,
        path: paths.packs,
    },
];

interface HomeTabsProps {
    journey: JOURNEYS;
    hasPacks: boolean;
    abTestPhoneVariant: VariationEnum;
}

const servesOwnCatalog = (journey: JOURNEYS): boolean => journey === JOURNEYS.PACKS;

const requiresNavigation = (from: JOURNEYS, to: JOURNEYS): boolean => servesOwnCatalog(from) || servesOwnCatalog(to);

export const HomeTabs = memo(function HomeTabs({ journey, hasPacks, abTestPhoneVariant }: Readonly<HomeTabsProps>) {
    const switchJourney = cartStore((state) => state.switchJourney);
    const router = useRouter();
    const { sendPageTag } = usePageTracking(journey, abTestPhoneVariant);

    const displayedTabs = useMemo(
        () => tabsContent.filter((tab) => tab.key !== JOURNEYS.PACKS || hasPacks),
        [hasPacks],
    );

    // Tabs served by their own catalog trigger a real navigation, prefetch them
    // so the switch stays as instant as the client-side BYOU <-> FAI one.
    useEffect(() => {
        displayedTabs
            .filter((tab) => tab.key !== journey && requiresNavigation(journey, tab.key))
            .forEach((tab) => router.prefetch(tab.path));
    }, [displayedTabs, journey, router]);

    const onChangeTabs = useCallback(
        (tab: TabsConfig) => {
            if (tab.key === journey) return;
            switchJourney(tab.key);

            // The target page remounts HomeTabs and sends its own hit.
            if (requiresNavigation(journey, tab.key)) {
                router.push(tab.path, { scroll: false });
                return;
            }

            sendPageTag(tab.key);
            replaceWindowUrl(tab.path);
        },
        [journey, switchJourney, router, sendPageTag],
    );

    return (
        <Tabs small align={Align.CENTER} activeIndex={displayedTabs.findIndex((tab) => tab.key === journey)}>
            <TabList>
                {displayedTabs.map((tab) => (
                    <Tab
                        testId={`home-tab-${tab.key}`}
                        key={tab.key}
                        iconName={tab.icon}
                        onClick={() => onChangeTabs(tab)}
                        id={tab.key}
                        label={tab.label}
                    />
                ))}
            </TabList>
        </Tabs>
    );
});
